const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeMessage = (message: string) => message.trim();

const collectStringMessages = (value: unknown): string[] => {
  if (typeof value === 'string') {
    const normalized = normalizeMessage(value);
    return normalized ? [normalized] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(collectStringMessages);
  }

  return [];
};

const extractValidationMessages = (errors: unknown): string[] => {
  if (Array.isArray(errors)) {
    return collectStringMessages(errors);
  }

  if (!isRecord(errors)) {
    return [];
  }

  return Object.values(errors).flatMap(collectStringMessages);
};

const uniqueMessages = (messages: string[]) => Array.from(new Set(messages));

const joinMessages = (messages: string[]) => uniqueMessages(messages).join('\n');

const getResponseStatus = (error: unknown): unknown => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return undefined;
  }

  return error.response.status;
};

const getResponseData = (error: unknown): unknown => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return undefined;
  }

  return error.response.data;
};

const extractBadRequestMessages = (data: unknown): string[] => {
  if (typeof data === 'string' || Array.isArray(data)) {
    return collectStringMessages(data);
  }

  if (!isRecord(data)) {
    return [];
  }

  const validationMessages = extractValidationMessages(data.errors);
  if (validationMessages.length > 0) {
    return validationMessages;
  }

  return ['message', 'detail', 'error', 'title'].flatMap(key => collectStringMessages(data[key]));
};

export function extractBadRequestErrorMessage(error: unknown, fallback: string): string {
  if (getResponseStatus(error) !== 400) {
    return fallback;
  }

  const message = joinMessages(extractBadRequestMessages(getResponseData(error)));
  return message || fallback;
}
